<template>
    <div>
        <div class="table table-sm mb-0 hide-header">
            <Draggable v-model="localValue"
                       :disabled="editingRowIndex >= 0"
                       handle=".drag-handle"
                       @end="onDragEnd"
                       tag="div"
                       class="table-body">
                <div v-for="(item, index) in localValue" :key="index"
                     class="table-row d-flex align-items-center">
                    <!-- Handle column -->
                    <div class="table-cell drag-handle-column">
                        <div class="drag-handle"
                             :class="{
                                 'text-muted': editingRowIndex >= 0,
                                 'locked-item': isItemLocked(item)
                             }"
                             :style="{ cursor: editingRowIndex >= 0 ? 'not-allowed' : 'move' }">
                            <i class="fas fa-grip-vertical"></i>
                        </div>
                    </div>

                    <!-- Name column -->
                    <div class="table-cell flex-fill">
                        <b-input v-model="editingRowData.name" v-if="editingRowIndex === index" autofocus maxlength="64" />
                        <span v-else>{{ item.name }}</span>
                    </div>

                    <!-- Offset Value column -->
                    <div class="table-cell offset-column" v-if="editingRowIndex === index || item.offsetValue">
                        <b-input v-model.number="editingRowData.offsetValue" v-if="editingRowIndex === index"
                                 type="number" min="1" placeholder="Offset" size="sm" />
                        <span v-else>{{ item.offsetValue || '' }}</span>
                    </div>

                    <!-- Offset Unit column -->
                    <div class="table-cell offset-unit-column" v-if="editingRowIndex === index || item.offsetUnit">
                        <b-select v-model="editingRowData.offsetUnit" v-if="editingRowIndex === index" size="sm">
                            <option :value="null">None</option>
                            <option value="Hours">Hours</option>
                            <option value="Days">Days</option>
                        </b-select>
                        <span v-else>{{ item.offsetUnit || '' }}</span>
                    </div>

                    <!-- Active column -->
                    <div class="table-cell active-column">
                        <b-check v-model="editingRowData.active" v-if="editingRowIndex === index" />
                        <span v-else>{{ item.active ? "✔️" : "" }}</span>
                    </div>

                    <!-- Actions column -->
                    <div class="table-cell actions">
                        <div class="d-flex" style="gap: 6px;">
                            <template v-if="editingRowIndex !== index">
                                <b-button v-if="!isItemLocked(item)" variant="link"
                                    @click.prevent="startTableEdit(item, index)" title="Edit">
                                    <b-icon icon="pencil" variant="primary" />
                                </b-button>
                                <b-button v-if="!item.id" variant="link"
                                    @click.prevent="removeTableItem(index)" title="Remove">
                                    <b-icon icon="x-circle-fill" variant="danger" />
                                </b-button>
                            </template>
                            <template v-else>
                                <b-button variant="link" title="Save" @click.prevent="saveTableEdit(index)"
                                    :disabled="saveTableDisabled">
                                    <b-icon icon="save" variant="primary" />
                                </b-button>
                                <b-button variant="link" title="Cancel Edit" @click.prevent="cancelTableEdit">
                                    <b-icon icon="x-lg" variant="" />
                                </b-button>
                            </template>
                        </div>
                    </div>
                </div>
            </Draggable>
        </div>

        <b-button size="sm" variant="primary" @click="addTableItem">
            {{ addLabel }}
        </b-button>
    </div>
</template>

<script lang="ts">
import { Vue, Prop, Component, Watch } from "vue-property-decorator";
import Draggable from 'vuedraggable';

interface OnHoldSubStatusItem {
    id?: number;
    name: string;
    active: boolean;
    offsetValue?: number | null;
    offsetUnit?: string | null;
}

@Component({
    name: "OnHoldSubStatusField",
    components: {
        Draggable
    }
})
export default class OnHoldSubStatusField extends Vue {
    @Prop({ default: () => [] }) value!: OnHoldSubStatusItem[];
    @Prop({ type: Array, default: () => [] }) lockedIds!: number[];
    @Prop({ type: String, required: true }) addLabel!: string;

    editingRowData: OnHoldSubStatusItem = { name: "", active: true, offsetValue: null, offsetUnit: null };
    editingRowIndex: number = -1;
    localValue: OnHoldSubStatusItem[] = [];

    @Watch('value', { immediate: true, deep: true })
    onValueChange(newValue: OnHoldSubStatusItem[]) {
        this.localValue = [...newValue];
    }

    get saveTableDisabled() {
        if (!this.editingRowData.name?.trim()) return true;
        // Offset value and unit must be set together or both empty
        const hasValue = this.editingRowData.offsetValue != null && this.editingRowData.offsetValue > 0;
        const hasUnit = !!this.editingRowData.offsetUnit;
        if (hasValue !== hasUnit) return true;
        return false;
    }

    isItemLocked(item: OnHoldSubStatusItem) {
        return this.lockedIds.includes(item.id || 0);
    }

    onDragEnd() {
        this.$emit('input', [...this.localValue]);
    }

    startTableEdit(item: OnHoldSubStatusItem, index: number) {
        this.editingRowIndex = index;
        this.editingRowData = { ...item };
    }

    addTableItem() {
        const cleanedArray = this.localValue.filter((a) => a.name.trim());
        cleanedArray.push({ name: "", active: true, offsetValue: null, offsetUnit: null });
        this.localValue = cleanedArray;
        this.$emit('input', [...this.localValue]);
        this.startTableEdit({ name: "", active: true, offsetValue: null, offsetUnit: null }, cleanedArray.length - 1);
    }

    removeTableItem(index: number) {
        const array = [...this.localValue];
        array.splice(index, 1);
        this.localValue = array;
        this.$emit('input', [...this.localValue]);
    }

    cancelTableEdit() {
        const editingIndex = this.editingRowIndex;
        const editingValue = this.localValue[editingIndex].name;
        if (!editingValue) {
            this.removeTableItem(editingIndex);
        }
        this.editingRowIndex = -1;
    }

    saveTableEdit(index: number) {
        const array = [...this.localValue];
        if (!this.editingRowData.name) {
            array.splice(index, 1);
        } else {
            array.splice(index, 1, this.editingRowData);
        }
        this.localValue = array;
        this.$emit('input', [...this.localValue]);
        this.editingRowIndex = -1;
    }
}
</script>

<style scoped lang="scss">
.table-body {
    display: block;
}

.table-row {
    border: 1px solid #dee2e6;
    border-top: 0;
    padding: 1px 7px;
    min-height: 38px;

    &:first-child {
        border-top: 1px solid #dee2e6;
    }

    &:hover {
        background-color: rgba(0, 0, 0, 0.075);
    }
}

.table-cell {
    display: flex;
    align-items: center;

    &.drag-handle-column {
        width: 30px;
        flex-shrink: 0;
        text-align: center;
        padding: 0;
    }

    &.actions {
        width: 100px;
        flex-shrink: 0;
        justify-content: flex-end;
        padding-left: 0.5rem;
    }

    &.active-column {
        width: 50px;
        flex-shrink: 0;
        text-align: center;
        padding: 0 0.25rem;
    }

    &.offset-column {
        width: 80px;
        flex-shrink: 0;
        padding: 0 0.25rem;
    }

    &.offset-unit-column {
        width: 100px;
        flex-shrink: 0;
        padding: 0 0.25rem;
    }

    &.flex-fill {
        flex: 1;
        padding-right: 0.5rem;
    }
}

.drag-handle {
    display: flex;
    align-items: center;
    justify-content: center;
    height: 100%;

    &.text-muted,
    &.drag-disabled {
        cursor: not-allowed !important;
        opacity: 0.5;
    }

    &.locked-item {
        color: #6c757d;
    }
}
</style>
